import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import type { BackofficeRole } from '@/contexts/BackofficeAuthContext';

interface MenuItem {
  id: string;
  title: string;
  path: string;
  icon: string;
  roles?: BackofficeRole[];
}

interface MenuGroup {
  id: string;
  title: string;
  icon: string;
  roles?: BackofficeRole[];
  items: MenuItem[];
}

interface BackofficeSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export default function BackofficeSidebar({ isCollapsed, onToggle }: BackofficeSidebarProps) {
  const { role, logout, session } = useBackofficeAuth();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['gestao']);
  const userInitial = session.nome?.charAt(0) || 'U';
  const userName = session.nome || 'Usuário';

  const toggleGroup = (groupId: string) => {
    if (isCollapsed) {
      onToggle();
      setTimeout(() => setExpandedGroups([groupId]), 300);
    } else {
      setExpandedGroups(prev =>
        prev.includes(groupId)
          ? prev.filter(id => id !== groupId)
          : [...prev, groupId]
      );
    }
  };

  const menuGroups: MenuGroup[] = [
    {
      id: 'gestao',
      title: 'Gestão',
      icon: 'ri-dashboard-line',
      items: [
        { id: 'dashboard', title: 'Dashboard', path: '/backoffice/dashboard', icon: 'ri-pulse-line' },
        { id: 'atendimento', title: 'Atendimento', path: '/backoffice/atendimento', icon: 'ri-customer-service-line' },
      ]
    },
    {
      id: 'financeiro',
      title: 'Financeiro',
      icon: 'ri-money-dollar-circle-line',
      roles: ['admin', 'financeiro'],
      items: [
        { id: 'fin-dashboard', title: 'Dashboard Financeiro', path: '/backoffice/financeiro/dashboard', icon: 'ri-bar-chart-grouped-line' },
        { id: 'fin-ops', title: 'Operações Pendentes', path: '/backoffice/financeiro', icon: 'ri-time-line' },
        { id: 'fin-manual', title: 'Operações Manuais', path: '/backoffice/financeiro/operacoes', icon: 'ri-exchange-dollar-line' },
      ]
    },
    {
      id: 'empresas',
      title: 'Empresas',
      icon: 'ri-building-line',
      items: [
        { id: 'emp-lista', title: 'Gestão de Empresas', path: '/backoffice/empresas', icon: 'ri-building-2-line' },
        { id: 'emp-config', title: 'Configurações', path: '/backoffice/empresas/configuracoes', icon: 'ri-equalizer-line' },
      ]
    },
    {
      id: 'comunicacao',
      title: 'Comunicação',
      icon: 'ri-notification-line',
      items: [
        { id: 'comunicados', title: 'Comunicados', path: '/backoffice/comunicados', icon: 'ri-mail-send-line' },
      ]
    },
    {
      id: 'auditoria',
      title: 'Auditoria',
      icon: 'ri-shield-check-line',
      items: [
        { id: 'audit-logs', title: 'Log de Ações', path: '/backoffice/auditoria', icon: 'ri-file-list-line' },
        { id: 'audit-config', title: 'Auditoria de Configs', path: '/backoffice/auditoria/configuracoes', icon: 'ri-settings-3-line', roles: ['admin'] },
      ]
    },
  ];

  const canSeeMenu = (item?: { roles?: BackofficeRole[] }) => {
    if (!item?.roles) return true;
    if (role === 'admin') return true;
    return item.roles.includes(role!);
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (items: MenuItem[]) => items.some(i => isActive(i.path));

  return (
    <aside className={`bg-stone-900 text-white h-screen overflow-y-auto transition-all duration-300 flex flex-col ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Logo Area */}
      <div className={`p-4 border-b border-white/10 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <div>
            <h2 className="text-lg font-bold text-white">Backoffice</h2>
            <p className="text-[10px] text-white/50">getPoints</p>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 hover:bg-white/10 rounded-lg transition-all duration-300 cursor-pointer"
          title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-${isCollapsed ? 'menu-unfold' : 'menu-fold'}-line text-xl text-white`}></i>
          </div>
        </button>
      </div>

      {/* Menu Groups */}
      <nav className="px-3 pt-4 flex-1">
        {menuGroups.map((group) => {
          if (!canSeeMenu(group)) return null;
          return (
            <div key={group.id} className="mb-4">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 rounded-lg text-left transition-all duration-300 cursor-pointer whitespace-nowrap mb-1 ${
                  isParentActive(group.items)
                    ? 'bg-emerald-600 text-white font-bold'
                    : 'text-white/70 hover:bg-white/10 hover:text-white font-semibold'
                }`}
                title={isCollapsed ? group.title : ''}
              >
                <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={group.icon}></i>
                  </div>
                  {!isCollapsed && <span className="text-sm">{group.title}</span>}
                </div>
                {!isCollapsed && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={expandedGroups.includes(group.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line text-xs'}></i>
                  </div>
                )}
              </button>

              {/* Group Items */}
              {!isCollapsed && expandedGroups.includes(group.id) && (
                <div className="ml-2 space-y-1">
                  {group.items.map((item) => {
                    if (!canSeeMenu(item)) return null;
                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-xs transition-all duration-300 cursor-pointer whitespace-nowrap ${
                          isActive(item.path)
                            ? 'bg-emerald-500/30 text-emerald-300 font-bold'
                            : 'text-white/60 hover:bg-white/5 hover:text-white/90 font-medium'
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={item.icon}></i>
                        </div>
                        <span>{item.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom: User Info + Logout */}
      <div className="p-3 border-t border-white/10">
        {!isCollapsed ? (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 px-2">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {userInitial}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{userName}</p>
                <p className="text-[10px] text-white/50 capitalize truncate">{role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all duration-300 cursor-pointer text-xs font-bold"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line"></i>
              </div>
              <span>Sair</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {userInitial}
              </span>
            </div>
            <button
              onClick={logout}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all duration-300 cursor-pointer"
              title="Sair"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}